import {
  Button,
  Col,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tooltip,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import {
  delDir,
  delIndicator,
  duplicateIndicator,
  getFolderRef,
  getIndexList,
  getIndicatorRef,
  moveIndicator,
  updateDir,
} from "./api";
import IndexEditModal from "./IndexEditModal";
import { IndexFilter, IndexFolder } from "./model";
import debounce from "lodash/debounce";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import MoveIndexFolder from "./components/MoveIndexFolder";
import EditFolderModal, {
  useFolderEdit,
} from "../components/folder/EditFolderModal";

const IndexManagement: React.FC = () => {
  const [refreshCount, setRefreshCount] = useState(0);
  const [search, setSearch] = useState<IndexFilter>({
    name: "",
    group: "",
    process: "",
  });
  // const [list, setList] = useState<IndexItem[]>([]);
  const [list, setList] = useState<IndexFolder[]>([]);
  const [editId, setEditId] = useState<number | undefined>();
  const [folderEdit, setFolderEdit] = useFolderEdit();

  const addFolder = () => {
    setFolderEdit.setIsEdit(false);
    setFolderEdit.setVisible(true);
  };

  const editFolder = (id: number, name: string) => {
    setFolderEdit.setId(id);
    setFolderEdit.setName(name);
    setFolderEdit.setIsEdit(true);
    setFolderEdit.setVisible(true);
  };
  const [moveFolderVisible, setMoveFolderVisible] = useState(false);
  const [moveFolderTarget, setMoveFolderTarget] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const columns = [
    {
      title: "指标名称",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "指标说明",
      dataIndex: "desc",
      key: "desc",
      render: (text) => {
        return text || "-";
      },
    },
    {
      title: "创建时间",
      dataIndex: "createTime",
      key: "createTime",
    },
    {
      title: "创建人",
      dataIndex: "creator",
      key: "creator",
    },
    {
      title: "操作",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => {
        if (record.type && record.type === "folder") {
          return (
            <Row>
              <Col>
                <Tooltip title={"编辑文件夹"}>
                  <Button
                    icon={<EditOutlined />}
                    type="link"
                    onClick={() => {
                      editFolder(record.id, record.name);
                    }}
                  ></Button>
                </Tooltip>
              </Col>
              <Col>
                <Tooltip title={"删除文件夹"}>
                  <Button
                    icon={<DeleteOutlined />}
                    type="link"
                    danger
                    onClick={() =>
                      onDeleteFolder(record.id, record.children.length || 0)
                    }
                  ></Button>
                </Tooltip>
              </Col>
            </Row>
          );
        }
        return (
          <Row>
            <Col>
              <Tooltip title={"编辑"}>
                <Button
                  icon={<EditOutlined />}
                  type="link"
                  onClick={() => edit(record.id)}
                ></Button>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={"复制"}>
                <Button
                  icon={<CopyOutlined />}
                  type="link"
                  onClick={() => copy(record.id)}
                ></Button>
              </Tooltip>
            </Col>
            <Tooltip title="移动指标">
              <Button
                icon={<FolderOpenOutlined />}
                type="link"
                onClick={() => {
                  setMoveFolderTarget(record.id);
                  setMoveFolderVisible(true);
                }}
              />
            </Tooltip>
            <Col>
              <Tooltip title={"删除"}>
                <Button
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  onClick={() => onDeleteIndicator(record.id)}
                ></Button>
              </Tooltip>
            </Col>
          </Row>
        );
      },
    },
  ];

  const onDeleteFolder = async (folderId: string, childrenNum: number) => {
    try {
      // 查询是否有依赖
      let refs = await getFolderRef(parseInt(folderId.substring(6)));
      // 去重
      const exi = new Set();
      if (Array.isArray(refs) && refs.length > 0) {
        refs = refs.reduce((acc, cur) => {
          if (!exi.has(cur.referenceId)) {
            exi.add(cur.referenceId);
            return acc.concat([cur]);
          } else {
            return acc;
          }
        }, []);
      }
      Modal.confirm({
        title: (
          <>
            {childrenNum > 0 && (
              <>
                删除文件夹将会删除该文件中的{childrenNum}条资源
                <br />
              </>
            )}
            确定删除文件夹吗?
          </>
        ),
        icon: <ExclamationCircleOutlined />,
        content: (
          <>
            {Array.isArray(refs) && refs.length > 0 && (
              <div>
                <div>删除后会导致以下引用内部指标的对象失效</div>
                {refs.map((ref, index) => {
                  return <div key={index}>指标订阅 : {ref.referenceName}</div>;
                })}
              </div>
            )}
          </>
        ),
        okText: "确定",
        okType: "danger",
        cancelText: "取消",
        onOk() {
          async () => {
            try {
              const id = folderId.substring(6);
              await delDir(parseInt(id));
            } catch (e) {
              console.error(e);
            } finally {
              afterEdit();
            }
          };
        },
        onCancel() {
          console.log("取消");
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onDeleteIndicator = async (id: number) => {
    try {
      // 查询是否有依赖
      const refs = await getIndicatorRef(id);
      Modal.confirm({
        title: <>确定要删除指标吗?</>,
        icon: <ExclamationCircleOutlined />,
        content: (
          <>
            {Array.isArray(refs) && refs.length > 0 && (
              <div>
                <div>删除后会导致以下引用该指标的对象失效</div>
                {refs.map((ref, index) => {
                  return <div key={index}>指标订阅：{ref.referenceName}</div>;
                })}
              </div>
            )}
          </>
        ),
        okText: "确定",
        okType: "danger",
        cancelText: "取消",
        onOk: async () => {
          try {
            await confirmDelete(id);
          } catch (e) {
            console.error(e);
          } finally {
            afterEdit();
          }
        },
        onCancel() {
          console.log("取消");
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const [loading, setLoading] = useState(false);

  const getList = async (params: IndexFilter) => {
    const res = await getIndexList(params);
    const parsed = res.map((dir) => {
      return {
        id: "folder" + dir.directoryId,
        name: dir.directoryName,
        type: "folder",
        createTime: dir.createdAt,
        creator: dir.creatorName,
        children: dir.indicators.map((item) => ({
          id: item.id,
          name: item.name,
          desc: item.comment,
          group: item.projectGroupName,
          process: item.bizProcess,
          createTime: item.createdAt,
          creator: item.creatorName,
        })),
      };
    });
    return parsed;
  };

  const reqList = useCallback(async (search) => {
    setLoading(true);
    const data = await getList(search);
    setList(data);
    if (search.name && search.name.length > 0) {
      setExpandedRowKeys(data.map((item) => item.id));
    } else {
      setExpandedRowKeys([]);
    }
    setLoading(false);
  }, []);

  const reqListDebounce = useCallback(debounce(reqList, 500), [reqList]);

  useEffect(() => {
    reqListDebounce(search);
  }, [search, reqListDebounce, refreshCount]);

  const createNewIndex = () => {
    setIndexEditModelVisible(true);
    setEditId(undefined);
  };

  const afterEdit = () => {
    setRefreshCount(refreshCount + 1);
  };

  const confirmDelete = (id: number) => {
    delIndicator(id)
      .then(() => {
        message.success("删除成功");
      })
      .finally(() => {
        afterEdit();
      });
  };

  const copy = (id: number) => {
    duplicateIndicator(id)
      .then(() => {
        afterEdit();
      })
      .catch((e) => {
        message.error("复制失败:" + e.toString());
      });
  };

  const edit = (id: number) => {
    setIndexEditModelVisible(true);
    setEditId(id);
  };

  const [scrollY, setScrollY] = useState(document.body.clientHeight - 285);
  // 监听窗口大小变化
  useEffect(() => {
    const onResize = () => {
      setScrollY(document.body.clientHeight - 285);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [indexEditModelVisible, setIndexEditModelVisible] = useState(false);

  const onCloseEdit = () => {
    setIndexEditModelVisible(false);
  };

  const onExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys(expandedRowKeys.concat([record.id]));
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key != record.id));
    }
  };

  return (
    <>
      <Row style={{ marginTop: "12px" }}>
        <Col>
          <Input.Search
            value={search.name}
            onChange={({ target: { value } }) => {
              setSearch({ ...search, name: value });
            }}
            placeholder="请输入指标名称"
            style={{ width: "100%" }}
          />
        </Col>
        <Col flex="auto"></Col>
        <Col style={{ paddingRight: "30px" }}>
          <Button type="default" onClick={() => addFolder()}>
            新建文件夹
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={createNewIndex}>
            新增指标
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: "12px" }}>
        <Col flex="100%">
          <Table
            columns={columns}
            dataSource={list}
            size="small"
            rowKey="id"
            scroll={{
              y: scrollY,
              x: 1000,
            }}
            bordered
            loading={{
              spinning: loading,
              indicator: <Spin />,
            }}
            expandable={{
              expandedRowKeys,
              onExpand,
            }}
          />
        </Col>
      </Row>
      {indexEditModelVisible && (
        <IndexEditModal
          visible={indexEditModelVisible}
          onClose={onCloseEdit}
          afterEdit={afterEdit}
          id={editId}
        />
      )}
      {folderEdit.visible && (
        <EditFolderModal
          open={folderEdit.visible}
          isEdit={folderEdit.isEdit}
          id={folderEdit.id}
          name={folderEdit.name}
          onConfirm={async function (params: {
            name: string;
            id?: number | undefined;
          }) {
            try {
              await updateDir(params);
            } catch (e) {
              console.error(e);
            } finally {
              setFolderEdit.setVisible(false);
              afterEdit();
            }
          }}
          onCancel={function (): void {
            setFolderEdit.setVisible(false);
          }}
        />
      )}
      {moveFolderVisible && (
        <MoveIndexFolder
          open={moveFolderVisible}
          onConfirm={async ({ folderId }) => {
            console.log("move", folderId, moveFolderTarget);
            setMoveFolderVisible(false);
            // 在此处移动
            try {
              await moveIndicator(moveFolderTarget, folderId);
            } catch (e) {
              console.error(e);
            } finally {
              setFolderEdit.setVisible(false);
              afterEdit();
            }
            afterEdit();
          }}
          onCancel={() => {
            setMoveFolderVisible(false);
          }}
        ></MoveIndexFolder>
      )}
    </>
  );
};

export default IndexManagement;
